import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Card,
  CardMedia,
  Fade,
  Tooltip,
  Modal,
  Button,
} from '@mui/material';
import { darken } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import useChat, { type Message } from '../hooks/useChat';
import { useCachedImages } from '../hooks/useCachedImages';
import { ThemeProvider } from '@mui/material/styles';
import NodeTheme from '../resources/NodeTheme';
import { useTranslation } from 'react-i18next';
import useActiveUser from '../collaboration/useActiveUser';
import MarkdownRenderer from './MarkdownRenderer';
import GradientBackground from '../resources/components/GradientBackground';

interface DocumentChatProps {
  threadId?: string;
  onClose?: () => void;
  open?: boolean;
  title?: string;
  onPreviousChat?: () => void;
  onNextChat?: () => void;
  hasPreviousChat?: boolean;
  hasNextChat?: boolean;
}

const DocumentChat: React.FC<DocumentChatProps> = ({
  threadId,
  onClose,
  open = true,
  title,
  onPreviousChat,
  onNextChat,
  hasPreviousChat,
  hasNextChat,
}) => {
  const { activeUser } = useActiveUser();
  const [inputText, setInputText] = useState('');
  // Removed unused calling state
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    addMessage,
    error,
    isInitialized,
    isWaitingForResponse,
    nextResponder,
    isLoadingInitialMessages,
    initialFetchError,
    retryInitialFetch,
  } = useChat({ threadId });

  // Removed unused activeOrganization

  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>(
    {}
  );
  const [loadedImages, setLoadedImages] = useState<Record<string, boolean>>({});
  const [openImageUrl, setOpenImageUrl] = useState<string | null>(null);

  const { get } = useCachedImages();
  const [cachedImageUrls, setCachedImageUrls] = useState<
    Record<string, string>
  >({});

  const { t } = useTranslation();

  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);

  // Add a new state to track animation
  const [isScrollButtonAnimating, setIsScrollButtonAnimating] = useState(false);

  // Format date for the date separator
  const formatMessageDate = (timestamp: number | null | undefined): string => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);

    // Check if date is today
    if (date.toDateString() === today.toDateString()) {
      return t('Today');
    }

    // Check if date is yesterday
    if (date.toDateString() === yesterday.toDateString()) {
      return t('Yesterday');
    }

    // Otherwise return formatted date
    return date.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Date separator component
  const DateSeparator = ({
    timestamp,
  }: {
    timestamp: number | null | undefined;
  }) => (
    <Box
      sx={{
        position: 'sticky',
        top: -8,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 1,
        width: '100%',
      }}
    >
      <Box
        sx={{
          backgroundColor: '#e8e8f0',
          px: 1.5,
          py: 0.75,
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: 'text.secondary',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {formatMessageDate(timestamp)}
        </Typography>
      </Box>
    </Box>
  );

  // Helper function to check if we need a date separator
  const shouldShowDateSeparator = (
    currentMessage: any,
    index: number
  ): boolean => {
    if (index === 0) return true; // Always show for first message

    const currentDate = new Date(
      (currentMessage as any).created_at || currentMessage.createdAt
    );
    const previousMessage = messages[index - 1] as any;
    const previousDate = new Date(
      previousMessage.created_at || previousMessage.createdAt
    );

    // Show separator if dates are different
    return currentDate.toDateString() !== previousDate.toDateString();
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  // Initial scroll when messages are first loaded
  useEffect(() => {
    if (!isLoadingInitialMessages && messages.length > 0) {
      scrollToBottom('instant');
    }
  }, [isLoadingInitialMessages]);

  // Update the scroll handler to respect animation
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    const handleScroll = () => {
      // Don't update if we're currently animating
      if (isScrollButtonAnimating) return;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
        setShowScrollButton(!isNearBottom);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [isScrollButtonAnimating]); // Add dependency

  // Update messages ref when messages change
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Auto-scroll on new messages
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if we're already near the bottom before scrolling
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;

    // Check if this is actually a new message
    const lastMessage = messages[messages.length - 1];
    const prevMessages = messagesRef.current;
    const isNewMessage =
      lastMessage &&
      // New message added
      (messages.length > prevMessages.length ||
        // Or waiting for response (user just sent message)
        isWaitingForResponse ||
        // Or response just arrived
        (prevMessages.length > 0 &&
          (lastMessage as any).internal_role === 'chatbot' &&
          lastMessage !== prevMessages[prevMessages.length - 1]));

    if (isNearBottom || isNewMessage) {
      scrollToBottom('smooth');
      // Hide scroll button when auto-scrolling
      setShowScrollButton(false);
    }
  }, [messages, isWaitingForResponse]);

  useEffect(() => {
    const loadCachedImages = async () => {
      const newLoadingState: Record<string, boolean> = {};
      const newLoadedState: Record<string, boolean> = {};

      for (const message of messages) {
        for (const content of message.content) {
          if (content.type === 'image_url') {
            const imageUrl = `${content.image_url?.url.replace(
              's3://',
              '/api/blob/'
            )}?redirect=true`;

            // Try to load from cache immediately
            const path = imageUrl.split('/api/blob/')[1].split('?')[0];
            const hash = path.split('/').pop()?.split('.')[0] || '';

            if (hash && path) {
              const cachedUrl = await get(hash, path);
              if (cachedUrl) {
                setCachedImageUrls((prev) => ({
                  ...prev,
                  [imageUrl]: cachedUrl,
                }));
                newLoadingState[imageUrl] = false;
                newLoadedState[imageUrl] = true;
              } else {
                newLoadingState[imageUrl] = true;
                newLoadedState[imageUrl] = false;
              }
            }
          }
        }
      }

      setLoadingImages(newLoadingState);
      setLoadedImages(newLoadedState);
    };

    loadCachedImages();
  }, [messages]);

  const handleSend = async () => {
    if (!inputText.trim()) return;

    const message: Message = {
      content: [{ type: 'text', text: inputText }],
      createdAt: Date.now(),
      role: 'user',
      metadata: {
        user_id: activeUser?.id,
        user_name: activeUser?.name,
      },
    };

    const success = await addMessage(message);
    if (success) {
      setInputText('');
    }
    // If not successful, the error will be handled by the useChat hook
    // and the message input will remain for the user to try again
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleImageLoad = (imageUrl: string) => {
    setLoadingImages((prev) => ({ ...prev, [imageUrl]: false }));
    setLoadedImages((prev) => ({ ...prev, [imageUrl]: true }));
  };

  const handleImageOpen = (url: string) => {
    setOpenImageUrl(url);
  };

  const handleImageClose = () => {
    setOpenImageUrl(null);
  };

  const loadImageFromCache = async (imageUrl: string) => {
    if (!cachedImageUrls[imageUrl]) {
      const path = imageUrl.split('/api/blob/')[1].split('?')[0];
      const hash = path.split('/').pop()?.split('.')[0] || '';

      if (hash && path) {
        try {
          const cachedUrl = await get(hash, path);
          if (cachedUrl) {
            if (isPDF(path)) {
              try {
                // For PDFs, we need to ensure proper content-type
                const response = await fetch(cachedUrl);
                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }
                const blob = await response.blob();
                const pdfBlob = new Blob([blob], { type: 'application/pdf' });
                const pdfUrl = URL.createObjectURL(pdfBlob);
                setCachedImageUrls((prev) => ({ ...prev, [imageUrl]: pdfUrl }));
              } catch (error) {
                console.error('Error processing PDF:', error);
                // Set loading state to false to remove loading indicator
                setLoadingImages((prev) => ({ ...prev, [imageUrl]: false }));
                return;
              }
            } else {
              setCachedImageUrls((prev) => ({
                ...prev,
                [imageUrl]: cachedUrl,
              }));
            }
            handleImageLoad(imageUrl);
          }
        } catch (error) {
          console.error('Error loading image from cache:', error);
          // Set loading state to false to remove loading indicator
          setLoadingImages((prev) => ({ ...prev, [imageUrl]: false }));
        }
      }
    }
  };

  const isPDF = (url: string) => {
    return url?.toLowerCase().endsWith('.pdf');
  };

  // Helper function to get message sender identifier
  const getMessageSender = (message: any) => {
    // Handle both server message format (internal_role) and local message format (role)
    if (message.internal_role === 'chatbot' || message.role === 'assistant') {
      return 'chatbot';
    }
    if (
      message.internal_role === 'accountant' ||
      message.internal_role === 'entrepreneur'
    ) {
      return message.internal_role;
    }
    return (
      message.user_name ||
      message.metadata?.user_name ||
      message.role ||
      'unknown'
    );
  };

  // Helper function to get message timestamp rounded to minute
  const getMessageMinute = (message: any) => {
    const date = new Date(message.created_at || message.createdAt);
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    );
  };

  // Helper function to determine if message should show metadata
  const shouldShowMessageMetadata = (message: any, index: number) => {
    if (index === messages.length - 1) {
      return true; // Always show for the last message
    }

    const currentSender = getMessageSender(message);
    const currentMinute = getMessageMinute(message);

    // Check if next message is from same sender and same minute
    const nextMessage = messages[index + 1];
    if (!nextMessage) return true;

    const nextSender = getMessageSender(nextMessage);
    const nextMinute = getMessageMinute(nextMessage);

    return (
      currentSender !== nextSender ||
      currentMinute.getTime() !== nextMinute.getTime()
    );
  };

  const getMessageColor = (message: any) => {
    // Handle both server message format (internal_role) and local message format (role)
    if (message.internal_role === 'chatbot' || message.role === 'assistant') {
      return '#F8F8FB'; // AI assistant stays white
    }

    if (message.internal_role === 'accountant') {
      return 'secondary.main';
    }

    return 'primary.main';
  };

  const renderMessageContent = (content: Message['content'][0]) => {
    switch (content.type) {
      case 'text':
        return (
          <MarkdownRenderer
            content={
              typeof content.text === 'string'
                ? content.text
                : content.text?.value || ''
            }
          />
        );

      case 'image_url':
        const imageUrl = `${content.image_url?.url.replace(
          's3://',
          '/api/blob/'
        )}?redirect=true`;

        if (loadingImages[imageUrl] === undefined) {
          setLoadingImages((prev) => ({ ...prev, [imageUrl]: true }));
          loadImageFromCache(imageUrl);
        }

        return (
          <Box
            sx={{
              position: 'relative',
              width: { xs: '180px', sm: '220px', md: '260px' },
              height: { xs: '180px', sm: '220px', md: '260px' },
              borderRadius: 1,
              overflow: 'hidden',
              my: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .zoom-overlay': {
                opacity: 1,
              },
            }}
          >
            <Fade in={loadingImages[imageUrl]} timeout={200}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 0.6 },
                    '50%': { opacity: 0.8 },
                    '100%': { opacity: 0.6 },
                  },
                }}
              >
                <CircularProgress
                  size={32}
                  sx={{ color: 'rgba(0, 0, 0, 0.2)' }}
                />
              </Box>
            </Fade>

            <Fade in={loadedImages[imageUrl]} timeout={300}>
              {isPDF(content.image_url?.url || '') ? (
                <iframe
                  src={`${cachedImageUrls[imageUrl]}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title="PDF Preview"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    overflow: 'hidden',
                  }}
                />
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    maxHeight: '100%',
                  }}
                >
                  <Box
                    component="img"
                    src={cachedImageUrls[imageUrl] || ''}
                    alt="Receipt"
                    onLoad={() => handleImageLoad(imageUrl)}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      backgroundColor: '#F8F8FB',
                      display: 'block',
                      margin: 'auto',
                    }}
                  />
                  <Box
                    className="zoom-overlay"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      borderRadius: 1,
                      pointerEvents: 'auto',
                    }}
                  >
                    <Tooltip title={t('View full size')}>
                      <IconButton
                        sx={{
                          width: { xs: 28, sm: 32, md: 36 },
                          height: { xs: 28, sm: 32, md: 36 },
                          backgroundColor: 'rgba(255, 255, 255, 0.92)',
                          '&:hover': {
                            backgroundColor: 'white',
                            transform: 'scale(1.05)',
                          },
                          transition: 'all 0.15s ease',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                        }}
                        onClick={() => handleImageOpen(imageUrl)}
                      >
                        <ZoomInIcon
                          sx={{ fontSize: { xs: 16, sm: 18, md: 20 } }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              )}
            </Fade>
          </Box>
        );

      case 'floness_interpretation':
        const interpretation = content.floness_interpretation;
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Tulkintani tositteesta: {interpretation?.description}
            </Typography>
            <Typography variant="body2">
              Summa:{' '}
              {new Intl.NumberFormat(undefined, {
                style: 'currency',
                currency: 'EUR',
              }).format(interpretation?.totalIncludingVAT || 0)}
            </Typography>
            <Typography variant="body2">
              Päivä: {new Date(interpretation?.date || '').toLocaleDateString()}
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  const isMessageContentPDF = (url: string | null) => {
    if (!url) return false;

    const message = messages.find((message) =>
      message.content.find(
        (content) =>
          content.type === 'image_url' &&
          content.image_url?.url ===
            url.replace('/api/blob/', 's3://')?.split('?')[0]
      )
    );

    if (!message) return false;

    const content = message.content.find(
      (content) =>
        content.type === 'image_url' &&
        content.image_url?.url ===
          url.replace('/api/blob/', 's3://')?.split('?')[0]
    );

    return isPDF(content?.image_url?.url || '');
  };

  const handleRetry = () => {
    retryInitialFetch();
  };

  // Show error message if there is one
  useEffect(() => {
    if (error) {
      // You could add a snackbar or toast notification here
      console.error('Chat error:', error);
    }
  }, [error]);

  useEffect(() => {
    console.log('showScrollButton changed to:', showScrollButton);
  }, [showScrollButton]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 0, sm: 3 },
        outline: 'none',
      }}
    >
      <Box
        sx={{
          height: { xs: '100vh', sm: 'calc(100vh - 48px)' },
          width: { xs: '100vw', sm: 'calc(100vw - 48px)' },
          maxWidth: { xs: '100vw', sm: '1000px' },
          display: 'flex',
          borderRadius: { xs: 0, sm: 2 },
          overflow: 'hidden',
          position: 'relative',
          boxShadow: { xs: 'none', sm: '0 8px 32px rgba(0, 0, 0, 0.12)' },
          outline: 'none',
        }}
      >
        <ThemeProvider theme={NodeTheme}>
          <GradientBackground
            variant="chat"
            sx={{
              height: '100%',
              width: '100%',
              display: 'flex',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {!threadId ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('No chat thread available')}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t(
                      'This activity entry or file does not have an associated chat thread yet.'
                    )}
                  </Typography>
                  {onClose && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={onClose}
                      sx={{ mt: 2 }}
                    >
                      {t('Close')}
                    </Button>
                  )}
                </Box>
              ) : error ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="error"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('Something went wrong')}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {error}
                  </Typography>
                  {onClose && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={onClose}
                      sx={{ mt: 2 }}
                    >
                      {t('Close')}
                    </Button>
                  )}
                </Box>
              ) : isLoadingInitialMessages ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    px: { xs: 1.5, sm: 3 },
                  }}
                >
                  <CircularProgress size={40} />
                  <Typography
                    color="text.secondary"
                    align="center"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('Loading chat history...')}
                  </Typography>
                </Box>
              ) : initialFetchError ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: 2,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    variant="h6"
                    color="error"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {t('Failed to load chat history')}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontFamily: 'Inter' }}
                  >
                    {initialFetchError}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleRetry}
                    sx={{ mt: 2 }}
                  >
                    {t('Retry')}
                  </Button>
                </Box>
              ) : (
                <>
                  {/* Header Bar with Title */}
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 20,
                      backgroundColor: '#FFFFFF',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                      px: { xs: 1, sm: 3 },
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '48px',
                      gap: { xs: 0.5, sm: 1 },
                      boxSizing: 'border-box',
                    }}
                  >
                    {/* Left side - Close button */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {onClose && (
                        <IconButton
                          onClick={onClose}
                          sx={{
                            color: 'primary.main',
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Center - Title */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexGrow: 1,
                        flexShrink: 1,
                        minWidth: 0, // Allow shrinking
                        px: { xs: 0.5, sm: 2 },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          color: 'text.primary',
                          fontSize: { xs: '1.125rem', sm: '1.25rem' },
                          lineHeight: 1.2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          textAlign: 'center',
                          minWidth: 0,
                        }}
                      >
                        {title || t('New chat')}
                      </Typography>
                    </Box>

                    {/* Right side - Navigation arrows */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 0.5, sm: 1 },
                        flexShrink: 0,
                      }}
                    >
                      {hasPreviousChat && onPreviousChat && (
                        <IconButton
                          onClick={onPreviousChat}
                          sx={{
                            color: 'primary.main',
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <ChevronLeftIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      )}
                      {hasNextChat && onNextChat && (
                        <IconButton
                          onClick={onNextChat}
                          sx={{
                            color: 'primary.main',
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <ChevronRightIcon sx={{ fontSize: 24 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>

                  {/* Messages Wrapper Container */}
                  <Box
                    sx={{
                      flex: 1,
                      position: 'relative',
                      overflowY: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Floating Next Responder Display */}
                    {nextResponder && (
                      <Box
                        sx={{
                          position: 'sticky',
                          top: 8,
                          zIndex: 15,
                          display: 'flex',
                          justifyContent: 'center',
                          width: '100%',
                          mb: 0,
                        }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            py: 0.75,
                            px: 2,
                            borderRadius: 2,
                            backgroundColor: (theme) =>
                              theme.palette.primary.main,
                            color: 'primary.contrastText',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                          }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            {nextResponder === 'accountant'
                              ? t('Waiting for accountant...')
                              : nextResponder === 'entrepreneur'
                                ? t('Waiting for entrepreneur...')
                                : t('Assistant is thinking...')}
                          </Typography>
                        </Paper>
                      </Box>
                    )}

                    {/* Messages Container */}
                    <Box
                      ref={messagesContainerRef}
                      sx={{
                        flex: 1,
                        px: { xs: 1.5, sm: 3 },
                        pt: { xs: 1.5, sm: 3 },
                        pb: { xs: '84px', sm: '96px' },
                        mt: nextResponder ? 2 : 0, // Add top margin when nextResponder is displayed
                        display: 'flex',
                        flexDirection: 'column',
                        gap: { xs: 0.5, sm: 1 }, // Reduced gap between messages
                        position: 'relative',
                        scrollBehavior: 'smooth',
                        minHeight: 0, // This is crucial for flex child scrolling
                        overflowY: 'auto', // Make this container scrollable
                      }}
                    >
                      {messages.map((message: any, index) => (
                        <React.Fragment key={index}>
                          <Fade in timeout={300}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems:
                                  message.internal_role === 'accountant' ||
                                  message.internal_role === 'entrepreneur' ||
                                  message.role === 'user'
                                    ? 'flex-end'
                                    : 'flex-start',
                                mb: 0.25,
                                position: 'relative',
                              }}
                            >
                              {/* Floating date separator - only show when needed */}
                              {shouldShowDateSeparator(message, index) && (
                                <DateSeparator
                                  timestamp={
                                    message.created_at || message.createdAt
                                  }
                                />
                              )}

                              <Paper
                                elevation={0}
                                sx={{
                                  p: { xs: 1.5, sm: 2 },
                                  bgcolor: getMessageColor(message),
                                  color:
                                    message.internal_role === 'chatbot' ||
                                    message.role === 'assistant'
                                      ? 'text.primary'
                                      : 'primary.contrastText',
                                  borderRadius:
                                    message.internal_role === 'accountant' ||
                                    message.internal_role === 'entrepreneur' ||
                                    message.role === 'user'
                                      ? '16px 16px 0 16px' // Sharp bottom right
                                      : '16px 16px 16px 0', // Sharp bottom left
                                  position: 'relative',
                                  maxWidth: '100%',
                                }}
                              >
                                {message.content.map(
                                  (content: any, i: number) => (
                                    <Box key={i} sx={{ mb: 0 }}>
                                      {renderMessageContent(content)}
                                    </Box>
                                  )
                                )}
                              </Paper>

                              {/* User name and timestamp below the entire message - only show when appropriate */}
                              {shouldShowMessageMetadata(message, index) && (
                                <Box
                                  sx={{
                                    mt: 0.5,
                                    opacity: 0.7,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {message.internal_role === 'accountant' ||
                                    message.internal_role === 'entrepreneur' ||
                                    message.role === 'user'
                                      ? (
                                          message.user_name ||
                                          message.metadata?.user_name ||
                                          t('User')
                                        ).split(' ')[0]
                                      : 'DashDocs.AI'}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    •
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: 'text.secondary',
                                      fontSize: '0.75rem',
                                    }}
                                  >
                                    {new Date(
                                      message.created_at || message.createdAt
                                    ).toLocaleTimeString(undefined, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Fade>
                        </React.Fragment>
                      ))}

                      {/* Loading Indicator */}
                      {isWaitingForResponse && (
                        <Fade in timeout={200}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 2,
                              borderRadius: 3,
                              bgcolor: '#F8F8FB',
                              boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                              maxWidth: '60%',
                              alignSelf: 'flex-start',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                }}
                              >
                                {[0, 1, 2].map((i) => (
                                  <Box
                                    key={i}
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      animation:
                                        'bounce 1.4s infinite ease-in-out',
                                      animationDelay: `${i * 0.16}s`,
                                      opacity: 0.6,
                                      '@keyframes bounce': {
                                        '0%, 80%, 100%': {
                                          transform: 'scale(0)',
                                        },
                                        '40%': {
                                          transform: 'scale(1)',
                                        },
                                      },
                                    }}
                                  />
                                ))}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                              >
                                {t('Thinking...')}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                width: '100%',
                                height: 2,
                                bgcolor: 'primary.main',
                                opacity: 0.2,
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '30%',
                                  height: '100%',
                                  bgcolor: 'primary.main',
                                  animation:
                                    'loading 1.5s infinite ease-in-out',
                                },
                                '@keyframes loading': {
                                  '0%': {
                                    transform: 'translateX(-100%)',
                                  },
                                  '100%': {
                                    transform: 'translateX(400%)',
                                  },
                                },
                              }}
                            />
                          </Box>
                        </Fade>
                      )}

                      <div ref={messagesEndRef} />
                    </Box>
                  </Box>

                  {/* Scroll to bottom button */}
                  <Fade
                    in={showScrollButton}
                    timeout={300}
                    onEnter={() => setIsScrollButtonAnimating(true)}
                    onEntered={() => setIsScrollButtonAnimating(false)}
                    onExit={() => setIsScrollButtonAnimating(true)}
                    onExited={() => setIsScrollButtonAnimating(false)}
                  >
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 100,
                        right: 24,
                        zIndex: 2,
                      }}
                    >
                      <Tooltip title={t('Scroll to bottom')}>
                        <IconButton
                          onClick={() => scrollToBottom()}
                          sx={{
                            backgroundColor: 'primary.main',
                            color: 'primary.contrastText',
                            boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                            width: 40,
                            height: 40,
                            '&:hover': {
                              backgroundColor: (theme) =>
                                darken(theme.palette.primary.main, 0.1),
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s ease-in-out',
                          }}
                        >
                          <KeyboardArrowDownIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Fade>

                  {/* Add Modal for full-size image view */}
                  <Modal
                    open={!!openImageUrl}
                    onClose={handleImageClose}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        maxWidth: '90vw',
                        maxHeight: '90vh',
                      }}
                    >
                      <IconButton
                        onClick={handleImageClose}
                        sx={{
                          position: 'absolute',
                          right: -20,
                          top: -20,
                          bgcolor: '#FFFFFF',
                          boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                          transition: 'all 0.2s ease-in-out',
                          zIndex: 1,
                          '&:hover': {
                            bgcolor: 'grey.100',
                            transform: 'scale(1.1)',
                            boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                          },
                          '&:active': {
                            transform: 'scale(0.95)',
                          },
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                      <Card elevation={8}>
                        {isMessageContentPDF(openImageUrl) ? (
                          <Box
                            sx={{
                              width: '70vw',
                              height: '85vh',
                              position: 'relative',
                            }}
                          >
                            {loadingImages[openImageUrl || ''] ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <CircularProgress />
                              </Box>
                            ) : (
                              <iframe
                                src={`${cachedImageUrls[openImageUrl || '']}#toolbar=0&view=FitH`}
                                title="PDF document"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  border: 'none',
                                }}
                              />
                            )}
                          </Box>
                        ) : (
                          <CardMedia
                            component="img"
                            src={
                              cachedImageUrls[openImageUrl || ''] ||
                              openImageUrl ||
                              ''
                            }
                            alt="Full size receipt"
                            sx={{
                              maxWidth: '90vw',
                              maxHeight: '90vh',
                              objectFit: 'contain',
                            }}
                          />
                        )}
                      </Card>
                    </Box>
                  </Modal>

                  {/* Input Area */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: { xs: 12, sm: 24 },
                      left: { xs: 12, sm: 24 },
                      right: { xs: 12, sm: 24 },
                      zIndex: 10,
                    }}
                  >
                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyDown={handleKeyPress}
                      placeholder={t('Type a message...')}
                      variant="outlined"
                      size="medium"
                      disabled={!isInitialized || !!error}
                      InputProps={{
                        endAdornment: (
                          <IconButton
                            color="primary"
                            onClick={handleSend}
                            disabled={
                              !inputText.trim() ||
                              !isInitialized ||
                              !!error ||
                              isWaitingForResponse
                            }
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              m: 1,
                              '&:hover': {
                                backgroundColor: (theme) =>
                                  darken(theme.palette.primary.main, 0.2),
                              },
                              '&.Mui-disabled': {
                                bgcolor: 'action.disabledBackground',
                              },
                              width: 48,
                              height: 48,
                            }}
                          >
                            <SendIcon sx={{ fontSize: 24 }} />
                          </IconButton>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '36px',
                          boxShadow: '0 2px 3px rgba(0,0,0,0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '4px',
                          '& fieldset': {
                            borderRadius: '36px',
                          },
                          '&:hover fieldset': {
                            borderRadius: '36px',
                          },
                          '&.Mui-focused fieldset': {
                            borderRadius: '36px',
                          },
                          '& .MuiOutlinedInput-input': {
                            padding: '8px 14px',
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </Box>
          </GradientBackground>
        </ThemeProvider>
      </Box>
    </Modal>
  );
};

export default DocumentChat;
